import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EditableContent } from '../components/content/EditableContent'
import { ApplyForm } from '../components/landing/ApplyForm'
import { GuildRules } from '../components/landing/GuildRules'
import { upcomingRaidSlots, guildTimeLabel } from '../lib/raidSchedule'
import { YouTubeVideo } from '../components/landing/YouTubeVideo'
import { GUILD_VIDEOS, type GuildVideo } from '../lib/guildVideos'

/**
 * The front door: what a signed-out visitor sees at the site root, and what
 * anyone sees at /welcome (so officers can read and edit it).
 *
 * Deliberately short. The full rules live on Guild Info behind the login;
 * this is only what a stranger needs to decide whether to apply: who we
 * are, when we raid, how loot works, the rules they agree to, and the form. The prose sections are
 * site_content rows officers edit in place, so nothing here needs a deploy
 * to reword.
 */
const GUILD = {
  founded: 'January 2024',
  origin: 'Turtle WoW',
  faction: 'Horde',
  style: 'PvE',
  region: 'EU',
  serverShort: 'WoW Forever',
}

function RaidTimes() {
  const slots = useMemo(() => upcomingRaidSlots(), [])
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
  // English like the rest of the page, in the visitor's time zone. Leaving
  // the locale to the browser gives "tisdag" next to English prose.
  const day = new Intl.DateTimeFormat('en-GB', { weekday: 'long' })
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' })

  // Sorted by weekday in the visitor's week rather than "next raid first",
  // so the list reads like a schedule and does not reorder through the week
  const byWeekday = [...slots].sort((a, b) => {
    const monFirst = (d: Date) => (d.getDay() + 6) % 7
    return monFirst(a.start) - monFirst(b.start)
  })

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-3">
        {byWeekday.map(({ start, end, optional }) => (
          <div
            key={start.toISOString()}
            className={`bg-raven-dark rounded-lg p-4 text-center border ${optional ? 'border-dashed border-gray-700' : 'border-gray-800'}`}
          >
            <p className={`font-semibold ${optional ? 'text-gray-400' : 'text-gray-200'}`}>{day.format(start)}</p>
            <p className="text-gray-400 text-sm mt-1">
              {time.format(start)} – {time.format(end)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{optional ? 'Only when needed' : 'Main raid night'}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Shown in your time zone ({zone}). Guild time is {guildTimeLabel()}.
      </p>
    </div>
  )
}

export function Landing() {
  const trailer: GuildVideo = GUILD_VIDEOS.trailer
  const dkpRules: GuildVideo = GUILD_VIDEOS.dkpRules
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-16 pb-12 text-center">
        <img
          src="/raven-logo.png"
          alt=""
          className="w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-6 rounded-full object-cover"
        />
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-100 mb-4">Raven</h1>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-2">
          A {GUILD.serverShort} {GUILD.style} {GUILD.faction} guild · {GUILD.region} region
        </p>
        <p className="text-sm text-gray-500 max-w-2xl mx-auto mb-8">
          Founded on {GUILD.origin} in {GUILD.founded}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <a
            href="#apply"
            className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg text-white transition-colors font-medium"
          >
            Apply to join
          </a>
          <Link
            to="/login"
            className="border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-gray-100 px-6 py-3 rounded-lg transition-colors font-medium"
          >
            Member login
          </Link>
        </div>
      </section>

      {/* Trailer */}
      {trailer.id && (
        <section className="max-w-3xl mx-auto px-4 pb-12">
          <YouTubeVideo videoId={trailer.id} title={trailer.title} poster={trailer.poster} />
        </section>
      )}

      {/* Who we are */}
      <section className="max-w-3xl mx-auto px-4 py-12 border-t border-gray-800">
        <h2 className="text-2xl font-bold text-gray-100 mb-4">Who we are</h2>
        <div className="text-gray-400 leading-relaxed">
          <EditableContent
            contentKey="landing_intro"
            emptyHint="Describe the guild for people thinking about applying"
          />
        </div>
      </section>

      {/* Raid times */}
      <section className="max-w-3xl mx-auto px-4 py-12 border-t border-gray-800">
        <h2 className="text-2xl font-bold text-gray-100 mb-4">When we raid</h2>
        <RaidTimes />
      </section>

      {/* Loot */}
      <section className="max-w-3xl mx-auto px-4 py-12 border-t border-gray-800">
        <h2 className="text-2xl font-bold text-gray-100 mb-4">How loot works</h2>
        <div className="text-gray-400 leading-relaxed">
          <EditableContent
            contentKey="landing_loot"
            emptyHint="A short summary of the loot rules"
          />
        </div>
        {dkpRules.id && (
          <div className="mt-6">
            <p className="text-sm text-gray-500 mb-2">Rules and bidding with Raven DKP+, in six minutes:</p>
            <YouTubeVideo videoId={dkpRules.id} title={dkpRules.title} poster={dkpRules.poster} />
          </div>
        )}
      </section>

      {/* Rules - the sections of Guild Info an applicant agrees to */}
      <section id="rules" className="max-w-3xl mx-auto px-4 py-12 border-t border-gray-800 scroll-mt-20">
        <h2 className="text-2xl font-bold text-gray-100 mb-4">Guild rules</h2>
        <GuildRules />
      </section>

      {/* Apply */}
      <section id="apply" className="max-w-3xl mx-auto px-4 py-12 border-t border-gray-800 scroll-mt-20">
        <h2 className="text-2xl font-bold text-gray-100 mb-2">Apply to join</h2>
        <p className="text-gray-400 mb-6 text-sm">
          Raiders and social members are both welcome. Tell us who you play, confirm your Discord
          account, and an officer will get back to you there.
        </p>
        <ApplyForm />
      </section>
    </PublicLayout>
  )
}
